import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import {
  attendanceErrorMessage,
  eventTypeLabel,
  locationOutcomeLabel,
} from "@/lib/attendanceErrors";
import {
  downloadPdfFromDataUrl,
  downloadPngFromDataUrl,
  downloadSvgString,
} from "@/lib/qrDownload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, Download, History, MapPin, Pencil, Power, QrCode, Trash2, Users } from "lucide-react";

const SIGNAGE_PRESETS = [
  { id: "small", label: "Small" },
  { id: "letter", label: "Letter" },
  { id: "a4", label: "A4" },
  { id: "poster", label: "Poster" },
  { id: "custom", label: "Custom" },
];

function defaultSignage(siteName) {
  const name = (siteName || "site").trim() || "site";
  return {
    title: `Welcome to ${name}`,
    subtitle: "Scan to check in / check out",
    sizePreset: "small",
    customWidthPx: 512,
    logoUrl: null,
    logoScale: 0.2,
  };
}

function formatAttendanceMinutes(mins) {
  const n = Math.max(0, Number(mins) || 0);
  const h = Math.floor(n / 60);
  const m = n % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function presenceStatusLabel(status) {
  if (status === "open" || status === "checked_in") return "On Site";
  if (status === "closed" || status === "checked_out") return "Off Site";
  return status || "Off Site";
}

function formatAttendanceDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatAttendanceTime(value) {
  if (!value) return "—";
  return new Date(value).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function AttendanceDetail({ label, children }) {
  return (
    <div className="min-w-0 rounded-lg border border-border/70 bg-background/40 px-3 py-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 truncate text-sm font-medium text-foreground">{children || "—"}</p>
    </div>
  );
}

const TABS = [
  { id: "sites", label: "Sites", icon: MapPin },
  { id: "live", label: "Who's On Site", icon: Users },
  { id: "history", label: "History", icon: History },
];

function Spinner() {
  return (
    <div className="flex items-center justify-center py-20" role="status" aria-live="polite">
      <div
        className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"
        aria-hidden="true"
      />
      <span className="sr-only">Loading Smart Attendance</span>
    </div>
  );
}

async function copyText(text, successMsg) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(successMsg);
  } catch {
    toast.error("Could not copy to clipboard");
  }
}

export default function AttendancePage() {
  const [tab, setTab] = useState("sites");
  const [entitlement, setEntitlement] = useState(null);
  const [sites, setSites] = useState([]);
  const [live, setLive] = useState({ count: 0, sessions: [] });
  const [history, setHistory] = useState({
    sessions: [],
    events: [],
    daily_summaries: [],
    weekly_summaries: [],
  });
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [radius, setRadius] = useState("100");
  const [lunchGrace, setLunchGrace] = useState("60");
  const [filterSite, setFilterSite] = useState("");
  const [historyDateFrom, setHistoryDateFrom] = useState("");
  const [historyDateTo, setHistoryDateTo] = useState("");
  const [historyPersonType, setHistoryPersonType] = useState("");
  const [historyStatus, setHistoryStatus] = useState("");
  const [exportingPdf, setExportingPdf] = useState(false);
  const [minted, setMinted] = useState(null);
  const [preview, setPreview] = useState(null);
  const [signage, setSignage] = useState(defaultSignage(""));
  const [signageFilename, setSignageFilename] = useState(null);
  const [signageBusy, setSignageBusy] = useState(false);
  const signageDebounceRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [editSite, setEditSite] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    address: "",
    latitude: "",
    longitude: "",
    radius_m: "100",
    lunch_grace_minutes: "60",
  });
  const [contractorSite, setContractorSite] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [newContractor, setNewContractor] = useState("");
  const [orgSettings, setOrgSettings] = useState(null);
  const [savingPresence, setSavingPresence] = useState(false);

  const loadEntitlementAndSites = useCallback(async () => {
    setLoading(true);
    try {
      const ent = await api.get("/attendance/entitlement");
      setEntitlement(ent.data);
      if (ent.data.entitled) {
        const [list, settings] = await Promise.all([
          api.get("/attendance/sites"),
          api.get("/attendance/org-settings"),
        ]);
        setSites(list.data || []);
        setOrgSettings(settings.data || null);
      }
    } catch (err) {
      toast.error(attendanceErrorMessage(err, "Failed to load Smart Attendance"));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLive = useCallback(async () => {
    try {
      const params = filterSite ? `?site_id=${encodeURIComponent(filterSite)}` : "";
      const { data } = await api.get(`/attendance/live${params}`);
      setLive(data);
    } catch (err) {
      toast.error(attendanceErrorMessage(err, "Failed to load live attendance"));
    }
  }, [filterSite]);

  const loadHistory = useCallback(async () => {
    try {
      const qs = new URLSearchParams({ limit: "100" });
      if (filterSite) qs.set("site_id", filterSite);
      if (historyDateFrom) qs.set("date_from", historyDateFrom);
      if (historyDateTo) qs.set("date_to", historyDateTo);
      if (historyPersonType) qs.set("person_type", historyPersonType);
      if (historyStatus) qs.set("status", historyStatus);
      const { data } = await api.get(`/attendance/history?${qs}`);
      setHistory(data);
    } catch (err) {
      toast.error(attendanceErrorMessage(err, "Failed to load history"));
    }
  }, [filterSite, historyDateFrom, historyDateTo, historyPersonType, historyStatus]);

  async function downloadHistoryPdf() {
    if (historyDateFrom && historyDateTo && historyDateTo < historyDateFrom) {
      toast.error("End date must be on or after start date");
      return;
    }
    setExportingPdf(true);
    try {
      const qs = new URLSearchParams();
      if (filterSite) qs.set("site_id", filterSite);
      if (historyDateFrom) qs.set("date_from", historyDateFrom);
      if (historyDateTo) qs.set("date_to", historyDateTo);
      if (historyPersonType) qs.set("person_type", historyPersonType);
      if (historyStatus) qs.set("status", historyStatus);
      const { data, headers } = await api.get(`/attendance/history/export.pdf?${qs}`, {
        responseType: "blob",
      });
      const disposition = headers["content-disposition"] || "";
      const matchedName = disposition.match(/filename="?([^";]+)"?/i)?.[1];
      const filename = matchedName || `smart-attendance-history-${new Date().toISOString().slice(0, 10)}.pdf`;
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Attendance PDF downloaded");
    } catch (err) {
      toast.error(attendanceErrorMessage(err, "Could not download attendance PDF"));
    } finally {
      setExportingPdf(false);
    }
  }

  useEffect(() => {
    loadEntitlementAndSites();
  }, [loadEntitlementAndSites]);

  useEffect(() => {
    if (!entitlement?.entitled) return;
    if (tab === "live") loadLive();
    if (tab === "history") loadHistory();
  }, [tab, entitlement, loadLive, loadHistory]);

  async function lookupAddress(forEdit = false) {
    const query = (forEdit ? editForm.address : address).trim();
    if (query.length < 3) {
      toast.error("Enter a street address to look up");
      return;
    }
    setGeocoding(true);
    try {
      const { data } = await api.post("/attendance/geocode", { query });
      const lat = String(data.latitude);
      const lon = String(data.longitude);
      const resolved = data.display_name || query;
      if (forEdit) {
        setEditForm((f) => ({
          ...f,
          address: resolved,
          latitude: lat,
          longitude: lon,
        }));
      } else {
        setAddress(resolved);
        setLatitude(lat);
        setLongitude(lon);
      }
      toast.success("Address found — coordinates filled in");
    } catch (err) {
      toast.error(attendanceErrorMessage(err, "Could not find that address"));
    } finally {
      setGeocoding(false);
    }
  }

  async function savePresenceMode(mode) {
    setSavingPresence(true);
    try {
      const { data } = await api.patch("/attendance/org-settings", {
        presence_verification_mode: mode,
      });
      setOrgSettings(data);
      if (data.coming_soon) {
        toast.message("Live Presence — Coming Soon", {
          description:
            "Your preference is saved. Standard verification (GPS at check-in, lunch return, and check-out) stays active until Live Presence ships.",
        });
      } else {
        toast.success("Presence verification set to Standard");
      }
    } catch (err) {
      toast.error(attendanceErrorMessage(err, "Could not update presence settings"));
    } finally {
      setSavingPresence(false);
    }
  }

  async function createSite(e) {
    e.preventDefault();
    if (!latitude || !longitude) {
      toast.error("Look up an address or enter latitude and longitude");
      return;
    }
    setBusy(true);
    try {
      await api.post("/attendance/sites", {
        name,
        address: address.trim() || null,
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
        radius_m: parseInt(radius, 10) || 100,
        lunch_grace_minutes: parseInt(lunchGrace, 10) || 60,
        gps_policy: "strict",
      });
      toast.success("Site created");
      setName("");
      setAddress("");
      setLatitude("");
      setLongitude("");
      setRadius("100");
      setLunchGrace("60");
      await loadEntitlementAndSites();
    } catch (err) {
      toast.error(attendanceErrorMessage(err, "Could not create site"));
    } finally {
      setBusy(false);
    }
  }

  function openEdit(site) {
    setEditSite(site);
    setEditForm({
      name: site.name || "",
      address: site.address || "",
      latitude: String(site.latitude ?? ""),
      longitude: String(site.longitude ?? ""),
      radius_m: String(site.radius_m ?? 100),
      lunch_grace_minutes: String(site.lunch_grace_minutes ?? 60),
    });
  }

  async function saveEdit(e) {
    e.preventDefault();
    if (!editSite) return;
    setBusy(true);
    try {
      await api.patch(`/attendance/sites/${editSite.id}`, {
        name: editForm.name.trim(),
        address: editForm.address.trim() || null,
        latitude: parseFloat(editForm.latitude),
        longitude: parseFloat(editForm.longitude),
        radius_m: parseInt(editForm.radius_m, 10) || 100,
        lunch_grace_minutes: parseInt(editForm.lunch_grace_minutes, 10) || 60,
      });
      toast.success("Site updated");
      setEditSite(null);
      await loadEntitlementAndSites();
    } catch (err) {
      toast.error(attendanceErrorMessage(err, "Could not update site"));
    } finally {
      setBusy(false);
    }
  }

  const buildSignagePayload = useCallback(
    (plainToken, format, overrides = {}) => {
      const s = { ...signage, ...overrides };
      const body = {
        plain_token: plainToken,
        format,
        style: "classic",
        title: s.title,
        subtitle: s.subtitle,
        logo_url: s.logoUrl || null,
        logo_scale: s.logoScale,
        size_preset: s.sizePreset,
      };
      if (s.sizePreset === "custom") {
        body.custom_width_px = Number(s.customWidthPx) || 512;
      }
      if (format === "pdf") {
        body.page = s.sizePreset === "a4" ? "a4" : "letter";
      }
      return body;
    },
    [signage]
  );

  const refreshSignagePreview = useCallback(
    async (plainToken, nextSignage) => {
      if (!plainToken) return;
      setSignageBusy(true);
      try {
        // Screen preview stays small; export buttons request the selected preset size.
        const { data } = await api.post(
          "/attendance/qr-preview",
          buildSignagePayload(plainToken, "png", {
            ...(nextSignage || {}),
            sizePreset: "small",
          })
        );
        setPreview(data?.data || null);
        if (data?.filename) {
          setSignageFilename(data.filename.replace(/\.png$/i, ""));
        }
      } catch {
        /* preview optional */
      } finally {
        setSignageBusy(false);
      }
    },
    [buildSignagePayload]
  );

  useEffect(() => {
    if (!minted?.plain_token) return undefined;
    if (signageDebounceRef.current) clearTimeout(signageDebounceRef.current);
    signageDebounceRef.current = setTimeout(() => {
      refreshSignagePreview(minted.plain_token);
    }, 350);
    return () => {
      if (signageDebounceRef.current) clearTimeout(signageDebounceRef.current);
    };
  }, [minted?.plain_token, signage, refreshSignagePreview]);

  async function mintQr(siteId) {
    setBusy(true);
    setPreview(null);
    setSignageFilename(null);
    try {
      const { data } = await api.post(`/attendance/sites/${siteId}/qr`);
      const nextSignage = defaultSignage(data.site_name);
      setMinted(data);
      setSignage(nextSignage);
      toast.success("New QR generated — download signage or copy the scan URL now");
      await refreshSignagePreview(data.plain_token, nextSignage);
      await loadEntitlementAndSites();
    } catch (err) {
      toast.error(attendanceErrorMessage(err, "Could not generate QR"));
    } finally {
      setBusy(false);
    }
  }

  async function downloadSignage(format) {
    if (!minted?.plain_token) {
      toast.error("Generate a QR first — exports are only available in this session");
      return;
    }
    setSignageBusy(true);
    try {
      const { data } = await api.post(
        "/attendance/qr-preview",
        buildSignagePayload(minted.plain_token, format)
      );
      const baseName = (data.filename || signageFilename || "attendance-qr").replace(
        /\.(svg|png|pdf)$/i,
        ""
      );
      if (format === "svg") {
        downloadSvgString(data.data, `${baseName}.svg`);
      } else if (format === "pdf") {
        downloadPdfFromDataUrl(data.data, `${baseName}.pdf`);
      } else {
        downloadPngFromDataUrl(data.data, `${baseName}.png`);
      }
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error(attendanceErrorMessage(err, `Could not export ${format.toUpperCase()}`));
    } finally {
      setSignageBusy(false);
    }
  }

  async function uploadSignageLogo(file) {
    if (!file || !minted?.plain_token) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload/qr_logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSignage((prev) => ({ ...prev, logoUrl: res.data.url, logoScale: prev.logoScale || 0.2 }));
      toast.success("Logo added to signage");
    } catch {
      toast.error("Logo upload failed");
    }
  }

  async function revokeQr(siteId) {
    if (!window.confirm("Revoke the active QR for this site? Workers will no longer be able to scan it.")) {
      return;
    }
    setBusy(true);
    try {
      await api.post(`/attendance/sites/${siteId}/qr/revoke`);
      toast.success("Active QR revoked");
      if (minted?.site_id === siteId) {
        setMinted(null);
        setPreview(null);
        setSignageFilename(null);
      }
      await loadEntitlementAndSites();
    } catch (err) {
      toast.error(attendanceErrorMessage(err, "Could not revoke QR"));
    } finally {
      setBusy(false);
    }
  }

  async function toggleEnabled(site) {
    const next = !site.enabled;
    if (!next && !window.confirm(`Disable “${site.name}”? Check-in will stop until you enable it again.`)) {
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/attendance/sites/${site.id}`, { enabled: next });
      toast.success(next ? "Site enabled" : "Site disabled");
      await loadEntitlementAndSites();
    } catch (err) {
      toast.error(attendanceErrorMessage(err, "Could not update site"));
    } finally {
      setBusy(false);
    }
  }

  async function forceCheckOut(session) {
    const name = session.worker_label || session.worker_name || "this worker";
    if (
      !window.confirm(
        `Check out ${name}? Use this if they forgot to sign out. Their Daily Pass will close.`
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await api.post(`/attendance/live/sessions/${session.session_id}/check-out`);
      toast.success(`${name} checked out`);
      await loadLive();
      if (tab === "history") await loadHistory();
    } catch (err) {
      toast.error(attendanceErrorMessage(err, "Could not check out worker"));
    } finally {
      setBusy(false);
    }
  }

  async function deleteSite(site) {
    if (site.enabled) {
      toast.error("Disable the site before deleting it");
      return;
    }
    if (
      !window.confirm(
        `Delete “${site.name}”? It will be removed from your site list. Past attendance history is kept.`
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await api.delete(`/attendance/sites/${site.id}`);
      toast.success("Site deleted");
      if (filterSite === site.id) setFilterSite("");
      await loadEntitlementAndSites();
    } catch (err) {
      toast.error(attendanceErrorMessage(err, "Could not delete site"));
    } finally {
      setBusy(false);
    }
  }

  async function openContractors(site) {
    setContractorSite(site);
    setNewContractor("");
    setBusy(true);
    try {
      const { data } = await api.get(`/attendance/sites/${site.id}/contractors`);
      setContractors(data || []);
    } catch (err) {
      toast.error(attendanceErrorMessage(err, "Failed to load contractors"));
      setContractorSite(null);
    } finally {
      setBusy(false);
    }
  }

  async function addContractor(e) {
    e.preventDefault();
    if (!contractorSite || !newContractor.trim()) return;
    setBusy(true);
    try {
      await api.post(`/attendance/sites/${contractorSite.id}/contractors`, {
        name: newContractor.trim(),
      });
      setNewContractor("");
      const { data } = await api.get(`/attendance/sites/${contractorSite.id}/contractors`);
      setContractors(data || []);
      toast.success("Contractor added");
    } catch (err) {
      toast.error(attendanceErrorMessage(err, "Could not add contractor"));
    } finally {
      setBusy(false);
    }
  }

  async function setContractorActive(row, active) {
    if (!contractorSite) return;
    setBusy(true);
    try {
      await api.patch(`/attendance/sites/${contractorSite.id}/contractors/${row.id}`, {
        active,
      });
      const { data } = await api.get(`/attendance/sites/${contractorSite.id}/contractors`);
      setContractors(data || []);
      toast.success(active ? "Contractor restored" : "Contractor deactivated");
    } catch (err) {
      toast.error(attendanceErrorMessage(err, "Could not update contractor"));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="p-6" data-testid="attendance-loading">
        <Spinner />
      </div>
    );
  }

  if (!entitlement?.entitled) {
    return (
      <div className="p-6 max-w-xl animate-fade-in" data-testid="attendance-upsell">
        <Card className="card-hover">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <QrCode size={22} aria-hidden="true" /> Smart Attendance
            </CardTitle>
            <CardDescription>{entitlement?.message}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              This premium add-on lets you create GPS-verified check-in QR codes for your sites.
              Contact support or an admin to enable it on your account.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-3xl animate-fade-in" data-testid="attendance-page">
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <MapPin size={24} className="text-primary" aria-hidden="true" />
          Smart Attendance
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage sites, see who&apos;s on site, and review recent check-ins.
        </p>
      </div>

      <div
        className="flex flex-wrap gap-2"
        data-testid="attendance-tabs"
        role="tablist"
        aria-label="Attendance sections"
      >
        {TABS.map((t) => (
          <Button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            variant={tab === t.id ? "default" : "outline"}
            size="sm"
            onClick={() => setTab(t.id)}
            data-testid={`attendance-tab-${t.id}`}
          >
            <t.icon size={14} className="mr-1.5" aria-hidden="true" />
            {t.label}
          </Button>
        ))}
      </div>

      {(tab === "live" || tab === "history") && (
        <div className="flex flex-wrap items-center gap-2">
          <Label htmlFor="attendance-site-filter" className="text-xs text-muted-foreground">
            Site filter
          </Label>
          <select
            id="attendance-site-filter"
            className="h-9 text-sm bg-background border border-border rounded-md px-3 py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={filterSite}
            onChange={(e) => setFilterSite(e.target.value)}
            data-testid="attendance-site-filter"
          >
            <option value="">All sites</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <Button
            size="sm"
            variant="outline"
            onClick={() => (tab === "live" ? loadLive() : loadHistory())}
            aria-label="Refresh list"
          >
            Refresh
          </Button>
        </div>
      )}

      {tab === "sites" && (
        <div className="space-y-6">
          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Presence verification</CardTitle>
              <CardDescription>
                Organization setting. How worker location is verified on site.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="presence-verification-mode">Mode</Label>
                <select
                  id="presence-verification-mode"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={orgSettings?.presence_verification_mode || "standard"}
                  disabled={savingPresence || !orgSettings}
                  onChange={(e) => savePresenceMode(e.target.value)}
                  data-testid="attendance-presence-mode"
                >
                  <option value="standard">
                    Standard — GPS at check-in, lunch return, and check-out
                  </option>
                  <option value="live_presence">Live Presence — Coming Soon</option>
                </select>
              </div>
              {orgSettings?.coming_soon ? (
                <p
                  className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                  data-testid="attendance-presence-coming-soon"
                >
                  Coming Soon — Live Presence (periodic GPS, geofence monitoring, and
                  presence alerts) is reserved for a future release. Standard
                  verification remains active; no background tracking runs today.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Standard captures GPS only at check-in, return from lunch, and
                  check-out. There is no background tracking.
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="text-lg font-heading">New site</CardTitle>
              <CardDescription>
                Enter a street address (recommended) or paste coordinates, then set the check-in radius.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={createSite} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="site-name">Site name</Label>
                  <Input
                    id="site-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="attendance-site-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="site-address">Address</Label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      id="site-address"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Street, city, country"
                      data-testid="attendance-site-address"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={busy || geocoding}
                      onClick={() => lookupAddress(false)}
                      data-testid="attendance-site-lookup"
                    >
                      {geocoding ? "Looking up…" : "Look up address"}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Look up fills latitude and longitude. You can still edit coordinates manually.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="lat">Latitude</Label>
                    <Input
                      id="lat"
                      inputMode="decimal"
                      value={latitude}
                      onChange={(e) => setLatitude(e.target.value)}
                      required
                      data-testid="attendance-site-lat"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lon">Longitude</Label>
                    <Input
                      id="lon"
                      inputMode="decimal"
                      value={longitude}
                      onChange={(e) => setLongitude(e.target.value)}
                      required
                      data-testid="attendance-site-lon"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="radius">Radius (m)</Label>
                  <Input
                    id="radius"
                    inputMode="numeric"
                    value={radius}
                    onChange={(e) => setRadius(e.target.value)}
                    data-testid="attendance-site-radius"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lunch-grace">Lunch grace (minutes)</Label>
                  <Input
                    id="lunch-grace"
                    inputMode="numeric"
                    value={lunchGrace}
                    onChange={(e) => setLunchGrace(e.target.value)}
                    data-testid="attendance-site-lunch-grace"
                  />
                  <p className="text-xs text-muted-foreground">
                    Temporary absences within this window keep the same check-in session (default 60).
                  </p>
                </div>
                <Button type="submit" className="btn-glow" disabled={busy || geocoding} data-testid="attendance-site-create">
                  Create site
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="space-y-3">
            <h2 className="font-heading font-semibold">Your sites</h2>
            {sites.length === 0 && (
              <p className="text-sm text-muted-foreground">No sites yet. Create one above, then generate a QR.</p>
            )}
            {sites.map((site) => (
              <Card key={site.id} className="card-hover" data-testid="attendance-site-card">
                <CardContent className="pt-5 space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium flex flex-wrap items-center gap-2">
                        {site.name}
                        <Badge variant="outline" className="text-[10px]">
                          {site.enabled ? "Enabled" : "Disabled"}
                        </Badge>
                        {site.enabled && site.has_active_qr ? (
                          <Badge className="text-[10px] bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                            QR active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] text-muted-foreground">
                            {site.enabled ? "No active QR" : "Inactive"}
                          </Badge>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {site.address ? (
                          <>{site.address} · </>
                        ) : null}
                        {site.latitude}, {site.longitude} · {site.radius_m}m radius
                        {typeof site.lunch_grace_minutes === "number" && (
                          <> · {site.lunch_grace_minutes}m lunch grace</>
                        )}
                        {typeof site.open_session_count === "number" && (
                          <> · {site.open_session_count} on site</>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      className="btn-glow"
                      disabled={busy || !site.enabled}
                      onClick={() => mintQr(site.id)}
                      data-testid="attendance-mint-qr"
                      aria-label={`Generate new QR for ${site.name}`}
                    >
                      <QrCode size={14} className="mr-1" aria-hidden="true" />
                      {site.has_active_qr ? "Generate new QR" : "Generate QR"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy || !site.has_active_qr}
                      onClick={() => revokeQr(site.id)}
                      aria-label={`Revoke QR for ${site.name}`}
                    >
                      Revoke QR
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => openContractors(site)}
                      aria-label={`Manage contractors for ${site.name}`}
                      data-testid="attendance-manage-contractors"
                    >
                      Contractors
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => openEdit(site)}
                      aria-label={`Edit ${site.name}`}
                    >
                      <Pencil size={14} className="mr-1" aria-hidden="true" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => toggleEnabled(site)}
                      aria-label={site.enabled ? `Disable ${site.name}` : `Enable ${site.name}`}
                    >
                      <Power size={14} className="mr-1" aria-hidden="true" />
                      {site.enabled ? "Disable" : "Enable"}
                    </Button>
                    {!site.enabled && (
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={busy}
                        onClick={() => deleteSite(site)}
                        aria-label={`Delete ${site.name}`}
                        data-testid="attendance-delete-site"
                      >
                        <Trash2 size={14} className="mr-1" aria-hidden="true" />
                        Delete
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {minted && (
            <Card data-testid="attendance-minted" className="border-primary/30">
              <CardHeader>
                <CardTitle className="text-lg font-heading">QR ready — signage</CardTitle>
                <CardDescription>
                  Download SVG/PNG/PDF now. The plain token is shown once; only a hash is stored.
                  Signage export is available in this session only.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5 text-sm">
                <div className="grid gap-5 lg:grid-cols-[minmax(0,240px)_1fr]">
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Preview
                    </p>
                    <div className="flex min-h-[240px] items-center justify-center rounded-md border border-border bg-white p-3">
                      {preview ? (
                        <img
                          src={preview}
                          alt={`Attendance QR signage for ${minted.site_name}`}
                          className="max-h-64 w-auto max-w-full"
                          data-testid="attendance-signage-preview"
                        />
                      ) : (
                        <p className="text-muted-foreground text-xs">
                          {signageBusy ? "Updating preview…" : "Preview unavailable"}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label htmlFor="signage-title">Welcome text</Label>
                        <Input
                          id="signage-title"
                          value={signage.title}
                          onChange={(e) =>
                            setSignage((prev) => ({ ...prev, title: e.target.value }))
                          }
                          data-testid="attendance-signage-title"
                        />
                      </div>
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label htmlFor="signage-subtitle">Subtitle</Label>
                        <Input
                          id="signage-subtitle"
                          value={signage.subtitle}
                          onChange={(e) =>
                            setSignage((prev) => ({ ...prev, subtitle: e.target.value }))
                          }
                          data-testid="attendance-signage-subtitle"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="signage-preset">Size</Label>
                        <select
                          id="signage-preset"
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={signage.sizePreset}
                          onChange={(e) =>
                            setSignage((prev) => ({ ...prev, sizePreset: e.target.value }))
                          }
                          data-testid="attendance-signage-preset"
                        >
                          {SIGNAGE_PRESETS.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {signage.sizePreset === "custom" && (
                        <div className="space-y-1.5">
                          <Label htmlFor="signage-custom-width">QR width (px)</Label>
                          <Input
                            id="signage-custom-width"
                            type="number"
                            min={256}
                            max={4096}
                            value={signage.customWidthPx}
                            onChange={(e) =>
                              setSignage((prev) => ({
                                ...prev,
                                customWidthPx: e.target.value,
                              }))
                            }
                            data-testid="attendance-signage-custom-width"
                          />
                        </div>
                      )}
                      <div className="space-y-1.5">
                        <Label htmlFor="signage-logo">Center logo</Label>
                        <Input
                          id="signage-logo"
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) uploadSignageLogo(file);
                            e.target.value = "";
                          }}
                          data-testid="attendance-signage-logo"
                        />
                        {signage.logoUrl && (
                          <div className="flex items-center gap-2 pt-1">
                            <img
                              src={signage.logoUrl}
                              alt=""
                              className="h-8 w-8 rounded border border-border object-contain bg-white"
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                setSignage((prev) => ({ ...prev, logoUrl: null }))
                              }
                            >
                              Remove logo
                            </Button>
                          </div>
                        )}
                      </div>
                      {signage.logoUrl && (
                        <div className="space-y-1.5">
                          <Label htmlFor="signage-logo-scale">
                            Logo size ({Math.round(signage.logoScale * 100)}%)
                          </Label>
                          <input
                            id="signage-logo-scale"
                            type="range"
                            min={0.1}
                            max={0.22}
                            step={0.01}
                            value={signage.logoScale}
                            onChange={(e) =>
                              setSignage((prev) => ({
                                ...prev,
                                logoScale: parseFloat(e.target.value),
                              }))
                            }
                            className="w-full"
                            data-testid="attendance-signage-logo-scale"
                          />
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => downloadSignage("svg")}
                        disabled={signageBusy}
                        data-testid="attendance-signage-download-svg"
                      >
                        <Download size={14} className="mr-1" aria-hidden="true" />
                        Download SVG
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => downloadSignage("png")}
                        disabled={signageBusy}
                        data-testid="attendance-signage-download-png"
                      >
                        PNG
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => downloadSignage("pdf")}
                        disabled={signageBusy}
                        data-testid="attendance-signage-download-pdf"
                      >
                        PDF
                      </Button>
                    </div>
                  </div>
                </div>

                <p>
                  <span className="text-muted-foreground">Site:</span> {minted.site_name}
                </p>
                <div className="flex flex-wrap items-start gap-2 break-all">
                  <p className="flex-1 min-w-0">
                    <span className="text-muted-foreground">Scan URL:</span> {minted.scan_url}
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyText(minted.scan_url, "Scan URL copied")}
                    aria-label="Copy scan URL"
                  >
                    <Copy size={14} className="mr-1" aria-hidden="true" />
                    Copy URL
                  </Button>
                </div>
                <p className="break-all" data-testid="attendance-plain-token">
                  <span className="text-muted-foreground">Token (once):</span> {minted.plain_token}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {tab === "live" && (
        <Card data-testid="attendance-live" className="card-hover">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Users size={18} aria-hidden="true" /> Who&apos;s On Site
            </CardTitle>
            <CardDescription>
              {live.count} {live.count === 1 ? "person" : "people"} on site right now
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {live.sessions.length === 0 && (
              <p className="text-sm text-muted-foreground">Nobody is checked in.</p>
            )}
            {live.sessions.map((s) => (
              <div
                key={s.session_id}
                className="rounded-xl border border-border bg-muted/20 p-4 shadow-sm"
                data-testid="attendance-live-row"
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Name
                    </p>
                    <p className="mt-1 text-lg font-semibold leading-tight text-foreground">
                      {s.worker_label || s.worker_name || s.worker_email || "Worker"}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {s.person_type_label || s.person_type || "—"}
                      {s.company_name ? ` · ${s.company_name}` : ""}
                      {s.contractor_name &&
                      s.person_type === "contractor" &&
                      s.contractor_name !== s.company_name
                        ? ` · Working for ${s.contractor_name}`
                        : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 sm:flex-col sm:items-end">
                    <Badge className="border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/10">
                      {presenceStatusLabel(s.status)}
                    </Badge>
                    {s.is_away && (
                      <Badge variant="outline" className="border-amber-500/40 text-amber-400">
                        Away for lunch
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  <AttendanceDetail label="Job Site">{s.site_name}</AttendanceDetail>
                  <AttendanceDetail label="Checked In">
                    {formatAttendanceTime(s.check_in_at)}
                  </AttendanceDetail>
                  <AttendanceDetail label="Date">
                    {formatAttendanceDate(s.check_in_at)}
                  </AttendanceDetail>
                  <AttendanceDetail label="Pass ID">{s.pass_id}</AttendanceDetail>
                </div>

                <div className="mt-4 flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => forceCheckOut(s)}
                    data-testid="attendance-live-force-checkout"
                    aria-label={`Check out ${s.worker_label || s.worker_name || "worker"}`}
                  >
                    Check out
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {tab === "history" && (
        <div className="space-y-4" data-testid="attendance-history">
          <Card className="card-hover">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading">History filters</CardTitle>
              <CardDescription>
                The screen and downloaded PDF use the same site, date, person, and status filters.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-1.5">
                  <Label htmlFor="attendance-history-from">From</Label>
                  <Input
                    id="attendance-history-from"
                    type="date"
                    value={historyDateFrom}
                    onChange={(e) => setHistoryDateFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="attendance-history-to">To</Label>
                  <Input
                    id="attendance-history-to"
                    type="date"
                    value={historyDateTo}
                    onChange={(e) => setHistoryDateTo(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="attendance-history-person">Person</Label>
                  <select
                    id="attendance-history-person"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    value={historyPersonType}
                    onChange={(e) => setHistoryPersonType(e.target.value)}
                  >
                    <option value="">Everyone</option>
                    <option value="employee">Employees</option>
                    <option value="contractor">Contractors</option>
                    <option value="visitor">Visitors</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="attendance-history-status">Status</Label>
                  <select
                    id="attendance-history-status"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    value={historyStatus}
                    onChange={(e) => setHistoryStatus(e.target.value)}
                  >
                    <option value="">All statuses</option>
                    <option value="open">On Site</option>
                    <option value="closed">Off Site</option>
                  </select>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setFilterSite("");
                    setHistoryDateFrom("");
                    setHistoryDateTo("");
                    setHistoryPersonType("");
                    setHistoryStatus("");
                  }}
                >
                  Clear filters
                </Button>
                <Button onClick={downloadHistoryPdf} disabled={exportingPdf} className="btn-glow">
                  <Download size={16} className="mr-2" aria-hidden="true" />
                  {exportingPdf ? "Preparing PDF..." : "Download filtered PDF"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {(history.daily_summaries?.length > 0 || history.weekly_summaries?.length > 0) && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Card className="card-hover" data-testid="attendance-daily-summaries">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-heading">Daily attendance</CardTitle>
                  <CardDescription>Gross, break/absence deduction, and net (not payroll)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(history.daily_summaries || []).slice(0, 7).map((d) => (
                    <div key={d.day} className="text-xs border-b border-border py-2 last:border-0">
                      <p className="font-medium">{d.day}</p>
                      <p className="text-muted-foreground mt-0.5">
                        Gross {formatAttendanceMinutes(d.gross_attendance_minutes)}
                        {" · "}Deduction {formatAttendanceMinutes(d.deducted_absence_minutes)}
                        {" · "}Net {formatAttendanceMinutes(d.net_attendance_minutes)}
                      </p>
                    </div>
                  ))}
                  {(history.daily_summaries || []).length === 0 && (
                    <p className="text-sm text-muted-foreground">No daily totals yet.</p>
                  )}
                </CardContent>
              </Card>
              <Card className="card-hover" data-testid="attendance-weekly-summaries">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-heading">Weekly attendance</CardTitle>
                  <CardDescription>Week starts Monday (UTC day keys)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(history.weekly_summaries || []).slice(0, 4).map((w) => (
                    <div key={w.week_start} className="text-xs border-b border-border py-2 last:border-0">
                      <p className="font-medium">Week of {w.week_start}</p>
                      <p className="text-muted-foreground mt-0.5">
                        Gross {formatAttendanceMinutes(w.gross_attendance_minutes)}
                        {" · "}Deduction {formatAttendanceMinutes(w.deducted_absence_minutes)}
                        {" · "}Net {formatAttendanceMinutes(w.net_attendance_minutes)}
                      </p>
                    </div>
                  ))}
                  {(history.weekly_summaries || []).length === 0 && (
                    <p className="text-sm text-muted-foreground">No weekly totals yet.</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Sessions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {history.sessions.length === 0 && (
                <p className="text-sm text-muted-foreground">No sessions yet.</p>
              )}
              {history.sessions.map((s) => (
                <div
                  key={s.session_id}
                  className="text-sm border-b border-border py-2 last:border-0"
                  data-testid="attendance-history-session"
                >
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">
                      {s.worker_label || s.worker_name || s.worker_email || "Worker"}
                    </span>
                    <Badge variant="outline" className="text-[10px] capitalize">
                      {presenceStatusLabel(s.status)}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {s.person_type_label || s.person_type || "—"}
                    {s.company_name ? ` · ${s.company_name}` : ""}
                    {s.pass_id ? ` · Pass ${s.pass_id}` : ""}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {s.site_name} · in {s.check_in_at ? new Date(s.check_in_at).toLocaleString() : "—"}
                    {s.check_out_at ? ` · out ${new Date(s.check_out_at).toLocaleString()}` : ""}
                  </p>
                  {(s.status === "closed" || s.gross_attendance_minutes != null) && (
                    <p className="text-xs text-muted-foreground mt-0.5" data-testid="attendance-session-totals">
                      Gross {formatAttendanceMinutes(s.gross_attendance_minutes)}
                      {" · "}Break/absence deduction {formatAttendanceMinutes(s.deducted_absence_minutes)}
                      {" · "}Net {formatAttendanceMinutes(s.net_attendance_minutes)}
                    </p>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Scan events</CardTitle>
              <CardDescription>Outcomes without exposing GPS coordinates</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {history.events.length === 0 && (
                <p className="text-sm text-muted-foreground">No events yet.</p>
              )}
              {history.events.map((e) => (
                <div
                  key={e.id}
                  className="text-sm border-b border-border py-2 last:border-0"
                  data-testid="attendance-history-event"
                >
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">{eventTypeLabel(e.event_type)}</span>
                    <span className="text-xs text-muted-foreground text-right">
                      {locationOutcomeLabel(e.location_outcome) || e.validation_status || "—"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {e.site_name} · {e.worker_label || e.worker_name || e.worker_email || "—"} ·{" "}
                    {e.event_at ? new Date(e.event_at).toLocaleString() : "—"}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog
        open={!!contractorSite}
        onOpenChange={(open) => {
          if (!open) setContractorSite(null);
        }}
      >
        <DialogContent className="max-w-md" data-testid="attendance-contractors-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Site contractors</DialogTitle>
            <DialogDescription>
              Managed list for {contractorSite?.name}. Workers pick from this list when checking
              in as contractors (Other remains available).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={addContractor} className="flex gap-2">
            <Input
              value={newContractor}
              onChange={(e) => setNewContractor(e.target.value)}
              placeholder="Contractor company name"
              data-testid="attendance-contractor-name"
            />
            <Button type="submit" disabled={busy || !newContractor.trim()} data-testid="attendance-contractor-add">
              Add
            </Button>
          </form>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {contractors.length === 0 && (
              <p className="text-sm text-muted-foreground">No contractors yet.</p>
            )}
            {contractors.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between gap-2 text-sm border-b border-border py-2 last:border-0"
              >
                <div>
                  <p className="font-medium">{c.name}</p>
                  <p className="text-xs text-muted-foreground">{c.active ? "Active" : "Inactive"}</p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => setContractorActive(c, !c.active)}
                >
                  {c.active ? "Deactivate" : "Restore"}
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setContractorSite(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editSite} onOpenChange={(open) => { if (!open) setEditSite(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit site</DialogTitle>
            <DialogDescription>Update address, coordinates, or radius.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-site-name">Site name</Label>
              <Input
                id="edit-site-name"
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-address">Address</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  id="edit-address"
                  value={editForm.address}
                  onChange={(e) => setEditForm((f) => ({ ...f, address: e.target.value }))}
                  placeholder="Street, city, country"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy || geocoding}
                  onClick={() => lookupAddress(true)}
                >
                  {geocoding ? "Looking up…" : "Look up"}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="edit-lat">Latitude</Label>
                <Input
                  id="edit-lat"
                  inputMode="decimal"
                  value={editForm.latitude}
                  onChange={(e) => setEditForm((f) => ({ ...f, latitude: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-lon">Longitude</Label>
                <Input
                  id="edit-lon"
                  inputMode="decimal"
                  value={editForm.longitude}
                  onChange={(e) => setEditForm((f) => ({ ...f, longitude: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-radius">Radius (m)</Label>
              <Input
                id="edit-radius"
                inputMode="numeric"
                value={editForm.radius_m}
                onChange={(e) => setEditForm((f) => ({ ...f, radius_m: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-lunch-grace">Lunch grace (minutes)</Label>
              <Input
                id="edit-lunch-grace"
                inputMode="numeric"
                value={editForm.lunch_grace_minutes}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, lunch_grace_minutes: e.target.value }))
                }
                required
                data-testid="attendance-edit-lunch-grace"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditSite(null)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-glow" disabled={busy || geocoding}>
                Save changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
